import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import type {
  AdminAgentFamily,
  AdminAgentInstance,
} from "@/queries/adminAgents";
import { useUpdateAgentInstance } from "@/queries/adminAgents";

import { disabledTemplatesForFamily, formatConfigValue } from "./utils";

interface AddInstanceDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  families: AdminAgentFamily[];
  instances: AdminAgentInstance[];
}

/**
 * "Add instance" in ADR-0013 terms: enable an already-declared-but-
 * disabled template. The KNOWN_INSTANCES map in topology.py is frozen
 * at code-import time; this dialog surfaces those templates filtered
 * by family. Parameters are echoed read-only — they're part of the
 * instance's identity and cannot be edited.
 */
export function AddInstanceDialog({
  open,
  onOpenChange,
  families,
  instances,
}: AddInstanceDialogProps) {
  const { t } = useTranslation("admin-agents");
  const [selectedSkill, setSelectedSkill] = useState<string>(
    () => families[0]?.skill ?? "",
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  const templates = useMemo(
    () => disabledTemplatesForFamily(instances, selectedSkill),
    [instances, selectedSkill],
  );

  const selectedTemplate = templates.find((tt) => tt.instance_id === selectedTemplateId);
  const mutation = useUpdateAgentInstance(selectedTemplate?.instance_id ?? "");

  const onFamilyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSkill(e.target.value);
    setSelectedTemplateId("");
  };

  const onSubmit = () => {
    if (!selectedTemplate) return;
    mutation.mutate(
      { enabled: true },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("addInstance.title")}</DialogTitle>
          <DialogDescription>{t("addInstance.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="add-instance-family">{t("addInstance.familyLabel")}</Label>
            <Select
              id="add-instance-family"
              value={selectedSkill}
              onChange={onFamilyChange}
            >
              {families.map((f) => (
                <option key={f.skill} value={f.skill}>
                  {f.skill}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="add-instance-template">
              {t("addInstance.templateLabel")}
            </Label>
            {templates.length === 0 ? (
              <Alert data-testid="add-instance-no-templates">
                <AlertDescription>{t("addInstance.noTemplates")}</AlertDescription>
              </Alert>
            ) : (
              <Select
                id="add-instance-template"
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                data-testid="add-instance-template-select"
              >
                <option value="">—</option>
                {templates.map((tpl) => (
                  <option key={tpl.instance_id} value={tpl.instance_id}>
                    {tpl.instance_id}
                  </option>
                ))}
              </Select>
            )}
            <p className="text-xs text-muted-foreground">
              {t("addInstance.templateHint")}
            </p>
          </div>

          {selectedTemplate ? (
            <div className="flex flex-col gap-1" data-testid="add-instance-parameters">
              <Label>{t("addInstance.parametersPreview")}</Label>
              <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(selectedTemplate.parameters).length === 0 ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  Object.entries(selectedTemplate.parameters).map(([k, v]) => (
                    <span
                      key={k}
                      className="rounded border border-dashed px-1.5 py-0.5 text-muted-foreground"
                    >
                      <span className="font-medium">{k}</span>: {formatConfigValue(v)}
                    </span>
                  ))
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("instance.parametersReadOnly")}
              </p>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("addInstance.cancel")}
          </Button>
          <Button
            disabled={!selectedTemplate || mutation.isPending}
            onClick={onSubmit}
            data-testid="add-instance-submit"
          >
            {mutation.isPending ? t("addInstance.submitting") : t("addInstance.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
